type FeatureFlag = {
  value: string;
  description: string;
};

/**
 * IMPORTANT, When adding a new feature flag, please add team, name, and description.
 *
 * Teams are ENG, SUPPORT, PRODUCT
 */
export const featureFlags: FeatureFlag[] = [
  {
    value: "production-canary-scripted-model",
    description:
      "(ENG, Patrick) Routes production canary model calls to embedded scripts, except eval and new-project-lifecycle. Off by default. Configure: https://us.posthog.com/project/17701/feature_flags/945434",
  },
  {
    value: "seo-roi-tracker",
    description:
      "(PRODUCT, Berkley) Enables report recipes, SEO ROI tracker setup, and HTML customer reviews in Insights. Off by default.",
  },
  {
    value: "prevent-theme-header-footer",
    description:
      "(SUPPORT) Prevents theme-defined headers and footers from being automatically applied.",
  },
  {
    value: "archived-pages",
    description:
      "(SUPPORT) Enables support for managing and restoring archived pages.",
  },

  {
    value: "component-previews",
    description:
      "(ENG, Gabe) Renders component JSON outside the canvas instead of screenshots.",
  },
  {
    value: "write-through-products-sync",
    description:
      "(ENG, Noah) When loading products, we request them from the storefront API if possible and write them through to the database.",
  },
  {
    value: "replo-agent-opus",
    description:
      "(ENG, Gabe) Uses Claude Opus 4.5 model for chat agent instead of Sonnet.",
  },
  {
    value: "import-site-components",
    description:
      "(PRODUCT, Yuxin) Import site components: the site component import on the Components page and in the Pages/Components dropdown, its preview pill, the Brand Studio promo, the bedrock import routes and the agent's import-site-components tool. Also turns on the Components page redesign, the header-and-footer picker in Page settings, the preset chip's placement, thumbnails and Add presets in page controls, and creating new pages inside the (site) route group.",
  },
  {
    value: "replo-home-recommendations",
    description:
      "(PRODUCT, Kevin) Once setup finishes, hands the agent home checklist over to agent-written goal recommendations for the project.",
  },
  {
    value: "site-reports",
    description:
      "(PRODUCT, Yuxin) Gates the website-builder Site Reports (site audits) UI. Off by default (disabled in production) while evidence collection is broken; Page Audit is unaffected.",
  },
  {
    value: "replo-agent-model",
    description:
      "(ENG, Gabe) Multivariate: the variant name sets the main chat agent's model. Variants match an AgentModelChoice or OpenRouter slug kebab-cased to PostHog's charset (glm-4-6, anthropic-claude-sonnet-5). An optional --<effort> suffix (low, medium, high, xhigh, max) also sets reasoning effort for the agents the flag picked the model for, e.g. sonnet-5-5--medium; a saved project effort still wins, and an unknown suffix or an effort the model does not offer keeps the model at its baked effort. Explicit client-set model overrides still win; non-Anthropic choices route via OpenRouter automatically. Configure: https://app.posthog.com/project/17701/feature_flags/843182",
  },
  {
    value: "agent-sonnet-5-5-1m-context",
    description:
      "(ENG, Gabe) Lifts Sonnet 5.5 (direct and OpenRouter) from the 200K sandbox context ceiling to its full 1M window, so its sessions compact at about 968K instead of 168K. Side-by-side test of whether compaction causes rework; a change reaches a warm box on its next coordinator env rebuild (about 6 hours, or an explicit env invalidation). Don't switch flagged users to a model with a smaller real window, such as Haiku 4.5, during the test: a session already past 200K then fails to compact.",
  },
  {
    value: "mini-app-database-record-tools",
    description:
      "(PRODUCT, Noah) Exposes the five mini-app database record MCP tools (create/read/update/query/delete) to the agent. Off by default; the six schema-management tools are always registered.",
  },
  {
    value: "google-drive-integration",
    description:
      "(PRODUCT, Kevin) Enables Google Drive connection setup, discovery, and tool execution for the user. Off by default while restricted OAuth scopes are retired.",
  },
  {
    value: "hubspot-integration",
    description:
      "(SUPPORT, Noah) Shows the HubSpot Connect button. HubSpot caps our app at 25 installs until it is listed on the HubSpot Marketplace, so support turns this on per customer and leaves it on; without it the HubSpot card says to reach out to support. UI only: the backend does not check it.",
  },
  {
    value: "messages-integration",
    description:
      "(PRODUCT, Kevin) Enables Messages connection setup, phone verification, and chat access for the user. Off by default.",
  },
  {
    value: "payments-app",
    description:
      "(ENG, Yuxin) Shows the Payouts tab in the Orders miniapp (balance, next payout tracker, payouts, bank account and payout schedule, disputes). Off until Stripe Connect payouts are verified with real money movement.",
  },
  {
    value: "ab-tests",
    description:
      "(PRODUCT, Yuxin) Turns on experiments: the Site Builder's Experiments pages and page-menu entries, experiment results in Insights, and the agent's experiment tools. Team-only until the SDK release with experiment routing reaches sites.",
  },
  {
    value: "sightglass",
    description:
      "(ENG, Yuxin) Shows the Sightglass miniapp (competitor Meta ads and landing pages) in the launcher, home, widget library and tab routing, including offers: the Calendar tab of saved brands' campaigns, a company's Offers section, Arena's Offers ranking, a landing page's Versions strip and the Sightglass offer MCP tools. Team-only until the corpus is seeded.",
  },
  {
    value: "heatmaps",
    description:
      "(ENG, Yuxin) Shows the Site Builder's Heatmap panel: click and scroll heat drawn on the page preview, split by device and A/B variant. Capture and ingest run regardless; this only hides the panel.",
  },
  {
    value: "agent-viewport-turn-screenshots",
    description:
      "(ENG, Yuxin) Captures a viewport DOM snapshot at agent turn end and uploads it for the admin session filmstrip.",
  },
  {
    value: "canvas",
    description:
      "(ENG, Yuxin) Shows the Canvas miniapp (the ad maker: infinite canvas, frames, templates, export) in the launcher and tab routing. Team-only until the editor is complete.",
  },
  {
    value: "artifacts",
    description:
      "(ENG, Noah) Enables artifact authoring and management in Files, the artifact creation skill, and artifact authoring tools. Shared artifact viewing remains available regardless of this flag.",
  },
  {
    value: "canvas-quiz",
    description:
      "(ENG, Yuxin) Shows the Quiz panel in Canvas: quiz concept steps, campaign sections, reference screenshots, and Build, which sends the selection to chat as a prompt. Needs the `canvas` flag too.",
  },
  {
    value: "catalog-products",
    description:
      "(PRODUCT, Yuxin) Enables the 'Choose from catalog' path in the Products miniapp (browse ready-made Printful catalog products, then customize the design). Off shows the disabled 'Coming soon' card.",
  },
  {
    value: "project-context",
    description:
      "(ENG, Yuxin) Branding opens the single project-context editor (draft/publish rows) instead of the brand directory for projects that have no project_context rows yet. Projects with rows always get the editor; reads never consult this flag. Team-only until the migration has run.",
  },
  {
    value: "v1-migration",
    description:
      "(PRODUCT, Patrick) Lets customers bring published Replo v1 pages into their Agent site from a Home card, and lets the agent migrate the rest on request.",
  },
  {
    value: "v1-migration-job-table",
    description:
      "(ENG, Patrick) Stores v1 migration jobs in the V1MigrationJob Postgres table as well as R2, and reads admin job history from the table first. Off by default; R2 stays written while on, so turning it off is a full rollback.",
  },
  {
    value: "sandbox-egress-allowlist",
    description:
      "(ENG, Andrew) Creates the actor's new sandboxes with the Daytona egress allowlist. Create-time only: existing boxes keep their network settings.",
  },
  {
    value: "insights-onboarding",
    description:
      '(PRODUCT, Yuxin) Asks "See what\'s working" in signup (Set up Insights / Set up later) with one connect screen per source, and gives Insights its Reports / Heatmaps / A/B tests / Setup tabs, the Setup checklist, and the pixel setup on Heatmaps and A/B tests for other websites. Off keeps signup and the Insights home as they were.',
  },
  {
    value: "onboarding-business-category",
    description:
      "(PRODUCT, Yuxin) Onboarding files the business under one of 20 categories, asks website-or-scratch first, says the category on the brand panel with a picker, and lets it steer the workflow set and Home art. Off keeps the previous website step and vertical rules.",
  },
  {
    value: "insights-benchmark-views",
    description:
      "(PRODUCT, Yuxin) Lets the agent write the BenchmarkRange, BenchmarkTable and BenchmarkTrend views into Insights reports. Off makes the report write check reject them, so the agent falls back to a TimeSeriesChart band. Saved reports that already use them still render either way.",
  },
  {
    value: "chat-insights-cards",
    description:
      "(PRODUCT, Yuxin) Lets the agent draw Insights cards (a number, a trend, a breakdown or a benchmark, written as insights.json nodes) in its chat reply with show-insights. Off makes the tool refuse, so the agent answers in text or with an Insights report. Cards already in a chat still render either way.",
  },
  {
    value: "artifact-styles",
    description:
      "(PRODUCT, Yuxin) Lets the agent set a `style` (editorial, newsprint, magazine, analyst, terminal, soft, bold) in an artifact's artifact.json, which restyles the whole artifact: its HTML pages, live reports and charts. Off makes the write check reject any style but default. Artifacts that already have a style keep it either way.",
  },
];

export type InAppAnnouncementFlag = `${string}-announcement-${string}`;
export type FeatureName =
  | FeatureFlag["value"]
  | `announcement_${string}`
  | InAppAnnouncementFlag;

// NOTE (Cole, 2026-07-20): email is the distinct_id everywhere (a UUID would change rollout buckets); person_properties covers unstored persons.
export function buildPosthogDecideRequestBody({
  token,
  email,
  groups,
}: {
  token: string;
  email: string;
  groups?: { project?: string; workspace?: string };
}) {
  return {
    token,
    distinct_id: email,
    person_properties: { email },
    ...(groups ? { groups } : {}),
  };
}
